import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import { classNames } from "@via/utils/helpers.tsx";
import { useCallback, useEffect } from "react";

/** This is an independent, controlled pagination component
 * that is disconnected from the Table component. It can be
 * used anywhere in the app as needed. **/

export default function IndependentPagination({
  data,
  page,
  pageSize,
  pageSizes = [5, 10, 20],
  setPage,
  setPageSize,
  total,
  itemLabel,
}: {
  data: unknown[];
  page: number;
  pageSize: number;
  pageSizes?: number[];
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  total?: number;
  /** When given, a "Showing 1 to 100 of 234 pipelines" summary is shown. */
  itemLabel?: { singular: string; plural: string };
}) {
  const itemCount = total ?? data.length;
  const pageCount = Math.ceil(itemCount / pageSize);
  const isFirstPage = page + 1 === 1;
  const isLastPage = page + 1 >= pageCount;
  const firstShown = itemCount === 0 ? 0 : page * pageSize + 1;
  const lastShown = Math.min((page + 1) * pageSize, itemCount);

  useEffect(() => {
    if (total == null) return;
    const maxPage = Math.max(0, pageCount - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [pageCount, page, setPage, total]);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
    },
    [setPageSize]
  );

  return (
    <div className="mb-8 mt-8 flex w-full flex-col items-center gap-2 text-xs sm:flex-row">
      <div className="mb-2 sm:mb-0 sm:mr-auto">
        <span className="mr-2">Items per page</span>
        <select
          id="pagination"
          className="w-12 rounded border border-gray-200 p-1 text-xs focus:ring-1 focus:ring-inset focus:ring-indigo-600"
          value={pageSize}
          onChange={e => {
            handlePageSizeChange(Number(e.target.value));
          }}>
          {pageSizes.map(pageSize => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
        {itemLabel && (
          <span className="ml-4" data-testid="pagination-summary">
            Showing {firstShown} to {lastShown} of {itemCount}{" "}
            {itemCount === 1 ? itemLabel.singular : itemLabel.plural}
          </span>
        )}
      </div>
      <div className="flex gap-2">
        {/* First Page Button */}
        <button
          className={classNames(
            isFirstPage
              ? "bg-gray-100"
              : "hover:curstor-pointer bg-gray-100 hover:bg-gray-200 hover:text-indigo-600",
            "rounded p-1 duration-300 ease-in-out"
          )}
          onClick={() => {
            handlePageChange(0);
          }}
          disabled={isFirstPage}
          aria-label="First page">
          <ChevronDoubleLeftIcon className="h-4 w-4" />
        </button>
        {/* Previous Page Button */}
        <button
          className={classNames(
            isFirstPage
              ? "bg-gray-100"
              : "bg-gray-100 hover:cursor-pointer hover:bg-gray-200 hover:text-indigo-600",
            "rounded p-1 duration-300 ease-in-out"
          )}
          onClick={() => {
            handlePageChange(Math.max(page - 1, 0));
          }}
          disabled={isFirstPage}
          aria-label="Previous page">
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        {/* Page Number Input */}
        <span className="flex items-center gap-1 text-xs">
          <input
            id="index"
            min={1}
            max={pageCount}
            type="number"
            value={page + 1}
            onChange={e => {
              const pageNum = e.target.value ? Number(e.target.value) - 1 : 0;
              handlePageChange(pageNum);
            }}
            className="h-6 rounded border p-1 text-xs focus:ring-1 focus:ring-inset focus:ring-indigo-600"
          />
          of {pageCount}
        </span>
        {/* Next Page Button */}
        <button
          className={classNames(
            isLastPage
              ? "bg-gray-100"
              : "bg-gray-100 hover:cursor-pointer hover:bg-gray-200 hover:text-indigo-600",
            "rounded p-1 duration-300 ease-in-out"
          )}
          onClick={() => {
            handlePageChange(Math.min(page + 1, pageCount - 1));
          }}
          disabled={isLastPage}
          aria-label="Next page">
          <ChevronRightIcon className="h-4 w-4" />
        </button>
        {/* Last Page Button */}
        <button
          className={classNames(
            isLastPage
              ? "bg-gray-100"
              : "bg-gray-100 hover:cursor-pointer hover:bg-gray-200 hover:text-indigo-600",
            "rounded p-1 duration-300 ease-in-out"
          )}
          onClick={() => {
            handlePageChange(pageCount - 1);
          }}
          disabled={isLastPage}
          aria-label="Last page">
          <ChevronDoubleRightIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
